import {
  Component,
  computed,
  inject,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { InboxService } from '../../../services/inbox.service';
import { MatBadge } from '@angular/material/badge';
import { MessagesService } from '../../../services/messages.service';
import { NgClass } from '@angular/common';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { ConfigService } from '../../../core/services/config.service';
import { ResponsiveService } from '../../../services/responsive.service';
import { MatTooltip } from '@angular/material/tooltip';
import { UserDetailsCardComponent } from '../user-details-card/user-details-card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ClickOutsideDirective } from '../../../directives/click-outside.directive';
import { NotificationsService } from '../../../services/notifications.service';
@Component({
  selector: 'app-header',
  imports: [
    MatToolbar,
    MatIcon,
    MatIconButton,
    RouterLink,
    MatBadge,
    NgClass,
    MatTooltip,
    TranslatePipe,
    UserDetailsCardComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  private readonly _featureFlagService = inject(FeatureFlagService);
  private readonly _configService = inject(ConfigService);
  private readonly _inboxService: InboxService = inject(InboxService);
  private readonly _responsiveService = inject(ResponsiveService);
  private readonly _messagesService: MessagesService = inject(MessagesService);
  private readonly _notificationsService = inject(NotificationsService);

  protected isXSmallViewport = computed(() =>
    this._responsiveService.isMobile(),
  );
  protected isBigLogo = computed(() => this._featureFlagService.isBigLogo());
  protected unread = signal<number | null>(null);
  protected unreadChatCount = signal<number | null>(null);
  public isSidebarOpen = model(false);
  public isUserCardOpen = model(false);
  protected showInbox = signal<boolean>(false);
  protected showChat = signal<boolean>(false);
  protected showUserCardButton = computed(
    () => !this._featureFlagService.isUserCardInMenuTree(),
  );
  protected showNotifications = signal<boolean>(false);
  protected unreadNotificationsCount = computed(() => {
    return this._notificationsService.count() > 9
      ? '9+'
      : this._notificationsService.count();
  });

  protected badgeHidden = computed(() => {
    return this.unreadNotificationsCount() === 0;
  });

  async ngOnInit() {
    this.showInbox.set(this._featureFlagService.isInboxEnabled());
    this.showChat.set(this._featureFlagService.isChatEnabled());
    this.showNotifications.set(
      this._featureFlagService.isNotificationsEnabled(),
    );

    if (this.showInbox()) {
      await this.getUnreadInboxCount();
    }
    if (this.showInbox()) {
      await this.getUnreadChatCount();
    }

    if (this.showInbox()) {
      this._inboxService.readStatusChanged.subscribe(async () => {
        await this.getUnreadInboxCount();
      });
    }

    if (this.showChat()) {
      this._messagesService.readStatusChanged.subscribe(async () => {
        await this.getUnreadChatCount();
      });
    }

    if (this.showNotifications()) {
      await this._notificationsService.initialize();
    }
  }

  public toggleSidebar() {
    this.isSidebarOpen.set(!this.isSidebarOpen());
  }

  public toggleUserCardOpen() {
    this.isUserCardOpen.set(!this.isUserCardOpen());
  }

  public inboxRoute() {
    return this._configService.inboxRoute;
  }

  public inboxName() {
    return this._configService.inboxName;
  }

  public chatRoute() {
    return this._configService.chatRoute;
  }

  public chatName() {
    return this._configService.chatName;
  }

  private async getUnreadInboxCount() {
    const unreadCount = await this._inboxService.getInboxCount(undefined, {
      read_status: false,
    });
    if (unreadCount > 0) {
      this.unread.set(unreadCount);
    } else {
      this.unread.set(null);
    }
  }

  private async getUnreadChatCount() {
    let unreadMessagesCount = null;
    if (this.showChat()) {
      unreadMessagesCount = await this._messagesService.getUnreadCount();
    }

    if (unreadMessagesCount && unreadMessagesCount > 0) {
      this.unreadChatCount.set(unreadMessagesCount);
    } else {
      this.unreadChatCount.set(null);
    }
  }
}
